import { Lock, AlertTriangle } from 'lucide-react';
import useAuthStore from '../stores/authStore';
import useLockdown from '../hooks/useLockdown';
import LockdownConfirmModal from './LockdownConfirmModal';
import StatSummaryRow from './StatSummaryRow';
import ShabbatModeButton from './ShabbatModeButton';
import '../styles/v2/pages/mobile-home.css';

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

// Simplified home screen for narrow viewports. No DashboardHeader (so no help
// "?" or Customize buttons), no modular widget grid. Just a welcome, the
// gated Emergency Lockdown card, and the 2x2 stat quad.
export default function MobileHome() {
  const { user, currentTenant, tenantVersion } = useAuthStore();

  const modules = currentTenant?.modules || {};
  const hasAcs = modules.hasAcs || false;
  const hasShabbatConfig = modules.hasShabbatConfig || false;
  const shabbatEnabled = modules.shabbatEnabled || false;
  const canLockdown = user?.canLockdown || false;
  const tenantReady = Boolean(currentTenant?.id) && tenantVersion > 0;

  const {
    isLockedDown, showLockdownConfirm, setShowLockdownConfirm, lockdownLoading, handleLockdown,
  } = useLockdown({ hasAcs, tenantVersion });

  const greeting = getGreeting();
  const firstName = (user?.name || user?.username || '').split(' ')[0] || '';

  return (
    <div className="mobile-home">
      <div className="mobile-home-header">
        <h1 className="mobile-home-greeting">{greeting}{firstName ? `, ${firstName}` : ''}</h1>
        {currentTenant?.name && <p className="mobile-home-tenant">{currentTenant.name}</p>}
      </div>

      {canLockdown && hasAcs && (
        <div className="lockdown-container">
          <div className={`lockdown-box ${isLockedDown ? 'active' : ''}`}>
            <div className="lockdown-content">
              <AlertTriangle size={24} />
              <div className="lockdown-text">
                <h3>Emergency Lockdown</h3>
                <p>{isLockedDown ? 'System is currently in lockdown mode' : 'Lock all doors immediately in case of emergency'}</p>
              </div>
            </div>
            <button
              className={`btn lockdown-btn ${isLockedDown ? 'btn-success' : 'btn-danger'}`}
              onClick={() => setShowLockdownConfirm(true)}
              disabled={lockdownLoading}
            >
              <Lock size={18} />
              {isLockedDown ? 'Release Lockdown' : 'Initiate Lockdown'}
            </button>
          </div>
        </div>
      )}

      {hasShabbatConfig && (user?.isPlatformAdmin || (shabbatEnabled && user?.canActivateShabbat)) && (
        <ShabbatModeButton />
      )}

      <StatSummaryRow tenantReady={tenantReady} tenantVersion={tenantVersion} />

      <LockdownConfirmModal
        show={showLockdownConfirm}
        isLockedDown={isLockedDown}
        loading={lockdownLoading}
        onConfirm={handleLockdown}
        onCancel={() => setShowLockdownConfirm(false)}
      />
    </div>
  );
}
